// FE-048 — Link an existing Account Document to a Commitment.
//
// Browses the Commitment's Account file cabinet (or the Plan year
// folder if the Commitment has one) and lets the broker pick a file
// to attach. The actual attach is just a ``FileLink`` row — the file
// stays in Account Documents, the Commitment just gains a reference.

import { ChevronRight, FileIcon, Folder, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  listDocuments,
  type DocumentHost,
  type DocumentLibraryListing,
  type DocumentNodeFile,
} from "@/components/documents";

import { useLinkCommitmentAttachmentMutation } from "./hooks";


export function LinkExistingDocumentModal({
  open, onClose, commitmentId, accountId, planYearId,
}: {
  open: boolean;
  onClose: () => void;
  commitmentId: string;
  accountId: string | null;
  /** When the Commitment has a Plan year, the modal still browses
   * the **Account** root so the broker can navigate the entire
   * file cabinet (Account-level folders + ``/plan-years/...``). The
   * Plan-year id is kept around for future deep-link starts but is
   * not used to scope the listing host. */
  planYearId: string | null;
}) {
  const [folder, setFolder] = useState<string>("/");
  const [listing, setListing] = useState<DocumentLibraryListing | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const linkMutation = useLinkCommitmentAttachmentMutation(commitmentId);

  // Always navigate the Account-level cabinet so the broker can drill
  // into ``/plan-years/{year}/...``, not just the plan-year subtree.
  // This matches the Account Documents tab the broker already uses.
  const host: DocumentHost | null = useMemo(() => {
    if (!accountId) return null;
    return { hostKind: "account", accountId };
  }, [accountId]);
  void planYearId;

  // Reset state on open/close so reopening lands at the cabinet root.
  useEffect(() => {
    if (!open) {
      setFolder("/");
      setListing(null);
      setError(null);
      setPendingId(null);
      return;
    }
  }, [open]);

  // Fetch on every folder change while open.
  useEffect(() => {
    if (!open || !host) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listDocuments(host, folder)
      .then((next) => {
        if (cancelled) return;
        setListing(next);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoading(false);
        setError(
          err instanceof Error ? err.message : "Couldn't load documents.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, host, folder]);

  function handleLink(file: DocumentNodeFile): void {
    if (!file.file_id) {
      // Folder marker (no underlying File). Shouldn't happen in this
      // listing because we only render files in the file branch, but
      // satisfy the narrowed type and the BE 404 path defensively.
      return;
    }
    setPendingId(file.node_id);
    setError(null);
    linkMutation.mutate(file.file_id, {
      onSuccess: () => {
        setPendingId(null);
        onClose();
      },
      onError: (err) => {
        setPendingId(null);
        setError(
          err instanceof Error
            ? err.message
            : "Couldn't link this document.",
        );
      },
    });
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="link-existing-document-title"
        data-testid="link-existing-document-modal"
        className="flex w-full max-w-xl flex-col rounded-lg border border-border-subtle bg-canvas shadow-xl"
      >
        <header className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
          <h2
            id="link-existing-document-title"
            className="text-base font-semibold text-text-primary"
          >
            Link existing document
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-md p-1 text-text-secondary hover:bg-recessed"
          >
            <X size={18} />
          </button>
        </header>
        <div className="flex flex-col gap-3 p-5 text-sm">
          {!host ? (
            <p className="text-xs text-text-tertiary">
              Set the Commitment's Account before linking documents.
            </p>
          ) : (
            <>
              <Breadcrumbs folder={folder} onSelect={setFolder} />
              {error && (
                <p
                  role="alert"
                  data-testid="link-existing-document-error"
                  className="rounded-md bg-ember-tint px-3 py-2 text-xs text-ember-ink"
                >
                  {error}
                </p>
              )}
              {loading && (
                <p
                  data-testid="link-existing-document-loading"
                  className="text-xs text-text-tertiary"
                >
                  Loading…
                </p>
              )}
              {!loading && listing && (
                <ul
                  role="list"
                  data-testid="link-existing-document-listing"
                  className="flex max-h-[55vh] flex-col divide-y divide-border-subtle overflow-y-auto"
                >
                  {listing.folders.map((f) => (
                    <li
                      key={f.path}
                      data-testid={`link-existing-document-folder-${f.name}`}
                      className="flex items-center gap-2 px-1 py-2"
                    >
                      <Folder
                        size={14} className="text-teal" aria-hidden="true"
                      />
                      <button
                        type="button"
                        onClick={() => setFolder(f.path)}
                        className="flex-1 text-left text-sm text-text-primary hover:underline"
                      >
                        {f.name}
                      </button>
                    </li>
                  ))}
                  {listing.files.map((f) => (
                    <li
                      key={f.node_id}
                      data-testid={`link-existing-document-file-${f.node_id}`}
                      className="flex items-center gap-2 px-1 py-2"
                    >
                      <FileIcon
                        size={14}
                        className="text-text-tertiary"
                        aria-hidden="true"
                      />
                      <span className="flex-1 truncate">{f.filename}</span>
                      <button
                        type="button"
                        data-testid={`link-existing-document-pick-${f.node_id}`}
                        onClick={() => handleLink(f)}
                        disabled={pendingId === f.node_id}
                        className="rounded-md border border-border-subtle px-2 py-1 text-xs text-text-primary hover:bg-recessed disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {pendingId === f.node_id ? "Linking…" : "Link"}
                      </button>
                    </li>
                  ))}
                  {listing.folders.length === 0
                    && listing.files.length === 0 && (
                    <li
                      data-testid="link-existing-document-empty"
                      className="px-1 py-3 text-xs italic text-text-tertiary"
                    >
                      No documents in this folder.
                    </li>
                  )}
                </ul>
              )}
            </>
          )}
        </div>
        <footer className="flex justify-end border-t border-border-subtle px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed"
          >
            Close
          </button>
        </footer>
      </section>
    </div>
  );
}


function Breadcrumbs({
  folder, onSelect,
}: {
  folder: string;
  onSelect: (path: string) => void;
}) {
  const parts = folder.split("/").filter(Boolean);
  const crumbs = [{ name: "Root", path: "/" }];
  for (let i = 0; i < parts.length; i += 1) {
    crumbs.push({
      name: parts[i],
      path: "/" + parts.slice(0, i + 1).join("/") + "/",
    });
  }
  return (
    <nav
      aria-label="Folder breadcrumbs"
      data-testid="link-existing-document-breadcrumbs"
      className="flex flex-wrap items-center gap-1 text-xs text-text-tertiary"
    >
      {crumbs.map((c, idx) => (
        <span key={c.path} className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => onSelect(c.path)}
            className="hover:text-text-primary"
          >
            {c.name}
          </button>
          {idx < crumbs.length - 1 && (
            <ChevronRight size={11} aria-hidden="true" />
          )}
        </span>
      ))}
    </nav>
  );
}
